import dayjs from 'dayjs';

// ── Colors & Constants ───────────────────────────────────────────────
export const RESOLUTION_COLORS = {
  '1080p': '#f97316',
  '720p': '#3b82f6',
  unknown: '#6b7280',
};

export const TYPE_COLORS = {
  video: '#f97316',
  presentation: '#3b82f6',
};

export const TOOLTIP_STYLE = {
  backgroundColor: '#1a1a2e',
  border: '1px solid #374151',
  borderRadius: '0.5rem',
  color: 'white',
};

export const AXIS_TICK = { fill: '#9ca3af', fontSize: 12 };

export const TIME_BUCKETS = [
  { label: '0-30s', min: 0, max: 30 },
  { label: '30-60s', min: 30, max: 60 },
  { label: '1-2m', min: 60, max: 120 },
  { label: '2-5m', min: 120, max: 300 },
  { label: '5-10m', min: 300, max: 600 },
  { label: '10-20m', min: 600, max: 1200 },
  { label: '20m+', min: 1200, max: Infinity },
];

// ── Date Picker Slot Props (dark theme) ──────────────────────────────
export const pickerSlotProps = {
  textField: {
    fullWidth: true,
    size: 'small',
    sx: {
      width: '100%',
      '& .MuiPickersOutlinedInput-root': { color: 'white' },
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(240, 103, 11, 0.3)',
        borderRadius: '12px',
        color: 'white',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
        '&.Mui-focused fieldset': { borderColor: '#f97316', borderWidth: '1px' },
      },
      '& .MuiInputLabel-root': {
        color: 'rgba(246, 135, 7, 0.8)',
        '&.Mui-focused': { color: '#f97316' },
      },
      '& .MuiOutlinedInput-input': {
        color: 'white',
        '&::placeholder': { color: 'rgba(156, 163, 175, 0.6)', opacity: 1 },
      },
      '& .MuiInputBase-input': { color: 'white !important' },
      '& .MuiIconButton-root': { color: 'rgba(156, 163, 175, 0.8)' },
      '& .MuiFormHelperText-root': { color: 'rgba(156, 163, 175, 0.6)', marginLeft: 0 },
    },
  },
  popper: {
    sx: {
      '& .MuiPaper-root': {
        backgroundColor: '#1a1a2e',
        color: 'white',
        border: '1px solid #374151',
      },
      '& .MuiPickersDay-root': { color: 'white' },
      '& .MuiPickersDay-root.Mui-selected': { backgroundColor: '#f97316' },
      '& .MuiPickersDay-root:hover': { backgroundColor: '#252542' },
      '& .MuiDayCalendar-weekDayLabel': { color: '#9ca3af' },
      '& .MuiPickersCalendarHeader-label': { color: 'white' },
      '& .MuiPickersArrowSwitcher-button': { color: '#9ca3af' },
      '& .MuiClock-pin, & .MuiClockPointer-root': { backgroundColor: '#f97316' },
      '& .MuiClockPointer-thumb': { borderColor: '#f97316' },
      '& .MuiClockNumber-root': { color: 'white' },
      '& .MuiMultiSectionDigitalClock-root': { color: 'white' },
      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(249,115,22,0.2)' },
      '& .MuiDialogActions-root .MuiButton-root': { color: '#f97316' },
    },
  },
};

// ── Date Presets ─────────────────────────────────────────────────────
export const getPresetDates = (preset) => {
  const now = dayjs();
  switch (preset) {
    case 'today':
      return { start: now.startOf('day'), end: now };
    case 'week':
      return { start: now.startOf('week').add(1, 'day'), end: now }; // Monday
    case 'month':
      return { start: now.startOf('month'), end: now };
    case '90days':
      return { start: now.subtract(90, 'day').startOf('day'), end: now };
    default:
      return { start: now.startOf('day'), end: now };
  }
};

// ── Date Helpers ─────────────────────────────────────────────────────
export const toMs = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Fetch completed tasks, loop through all pages, filter by date range client-side.
// Backend /admin/video-tasks has no date-range filter yet, so we filter here.
export const fetchTasksInRange = async (apiCall, startDate, endDate) => {
  const startMs = startDate.toDate().getTime();
  const endMs = endDate.toDate().getTime();
  const all = [];
  let page = 1;
  while (true) {
    const data = await apiCall(`/admin/video-tasks?page=${page}&page_size=100&status=completed`);
    const tasks = data.tasks || [];
    all.push(...tasks);
    const pg = data.pagination || data;
    const totalPages = pg.total_pages || 1;
    if (page >= totalPages) break;
    page++;
  }
  return all.filter((t) => {
    const ms = toMs(t.created_at);
    return ms != null && ms >= startMs && ms <= endMs;
  });
};

// ── Status & Type Helpers ────────────────────────────────────────────
export const isValidTask = (task) => {
  return task.status === 'completed';
};

export const getTaskType = (task) => {
  return task.metadata?.type === 'presentation' ? 'presentation' : 'video';
};

export const getResolution = (task) => {
  return task.metadata?.resolution || 'unknown';
};

export const getCompletionSeconds = (task) => {
  const endMs = toMs(task.task_completed_at) || toMs(task.updated_at);
  const startMs = toMs(task.created_at);
  if (!endMs || !startMs) return null;
  const seconds = (endMs - startMs) / 1000;
  return seconds > 0 ? seconds : null;
};

// Processing time: processing_started_at → task_completed_at (excludes queue wait)
export const getProcessingSeconds = (task) => {
  const startMs = toMs(task.processing_started_at);
  if (!startMs) return null; // field not yet available for this task
  const endMs = toMs(task.task_completed_at) || toMs(task.updated_at);
  if (!endMs) return null;
  const seconds = (endMs - startMs) / 1000;
  return seconds > 0 ? seconds : null;
};

// Queue time: created_at → processing_started_at
export const getQueueSeconds = (task) => {
  const createdMs = toMs(task.created_at);
  const processingMs = toMs(task.processing_started_at);
  if (!createdMs || !processingMs) return null;
  const seconds = (processingMs - createdMs) / 1000;
  return seconds >= 0 ? seconds : null;
};

// ── Regression Utilities ─────────────────────────────────────────────

export const CURVE_TYPES = [
  { key: 'linear', label: 'Linear' },
  { key: 'logarithmic', label: 'Logarithmic' },
  { key: 'power', label: 'Power' },
  { key: 'exponential', label: 'Exponential' },
];

// Helper: least-squares fit for y = a + b*X (transformed coordinates)
const leastSquares = (xs, ys) => {
  const n = xs.length;
  if (!n) return { a: 0, b: 0 };
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i]; sy += ys[i]; sxy += xs[i] * ys[i]; sxx += xs[i] * xs[i];
  }
  const denom = n * sxx - sx * sx;
  if (denom === 0) return { a: sy / n, b: 0 };
  const b = (n * sxy - sx * sy) / denom;
  const a = (sy - b * sx) / n;
  return { a, b };
};

// R² goodness of fit
const rSquared = (points, predictFn) => {
  if (points.length < 2) return 0;
  const meanY = points.reduce((s, p) => s + p.y, 0) / points.length;
  let ssTot = 0, ssRes = 0;
  for (const p of points) {
    ssTot += (p.y - meanY) ** 2;
    const predicted = predictFn(p.x);
    ssRes += (p.y - predicted) ** 2;
  }
  return ssTot === 0 ? 0 : 1 - ssRes / ssTot;
};

// Linear: y = slope * x + intercept
const fitLinear = (points) => {
  const { a, b } = leastSquares(points.map(p => p.x), points.map(p => p.y));
  const predict = (x) => b * x + a;
  return {
    type: 'linear', slope: b, intercept: a, sampleCount: points.length,
    predict,
    formula: () => {
      const sign = a >= 0 ? '+' : '-';
      return `${round3(b)} × chars ${sign} ${round1(Math.abs(a))}s`;
    },
    r2: rSquared(points, predict),
  };
};

// Logarithmic: y = a + b * ln(x)
const fitLogarithmic = (points) => {
  const valid = points.filter(p => p.x > 0);
  if (!valid.length) return emptyResult('logarithmic');
  const { a, b } = leastSquares(valid.map(p => Math.log(p.x)), valid.map(p => p.y));
  const predict = (x) => x > 0 ? a + b * Math.log(x) : 0;
  return {
    type: 'logarithmic', a, b, sampleCount: valid.length,
    predict,
    formula: () => {
      const sign = a >= 0 ? '+' : '-';
      return `${round3(b)} × ln(chars) ${sign} ${round1(Math.abs(a))}s`;
    },
    r2: rSquared(valid, predict),
  };
};

// Power: y = a * x^b  (fit via ln(y) = ln(a) + b*ln(x))
const fitPower = (points) => {
  const valid = points.filter(p => p.x > 0 && p.y > 0);
  if (!valid.length) return emptyResult('power');
  const { a: lnA, b } = leastSquares(valid.map(p => Math.log(p.x)), valid.map(p => Math.log(p.y)));
  const a = Math.exp(lnA);
  const predict = (x) => x > 0 ? a * Math.pow(x, b) : 0;
  return {
    type: 'power', a, b, sampleCount: valid.length,
    predict,
    formula: () => `${round3(a)} × chars^${round3(b)}`,
    r2: rSquared(valid, predict),
  };
};

// Exponential: y = a * e^(b*x)  (fit via ln(y) = ln(a) + b*x)
const fitExponential = (points) => {
  const valid = points.filter(p => p.y > 0);
  if (!valid.length) return emptyResult('exponential');
  const { a: lnA, b } = leastSquares(valid.map(p => p.x), valid.map(p => Math.log(p.y)));
  const a = Math.exp(lnA);
  const predict = (x) => a * Math.exp(b * x);
  return {
    type: 'exponential', a, b, sampleCount: valid.length,
    predict,
    formula: () => `${round3(a)} × e^(${round6(b)} × chars)`,
    r2: rSquared(valid, predict),
  };
};

const emptyResult = (type) => ({
  type, sampleCount: 0, predict: () => 0,
  formula: () => 'No data', r2: 0,
});

const round1 = (v) => Math.round(v * 10) / 10;
const round3 = (v) => Math.round(v * 1000) / 1000;
const round6 = (v) => Math.round(v * 1000000) / 1000000;

const FIT_FUNCTIONS = {
  linear: fitLinear,
  logarithmic: fitLogarithmic,
  power: fitPower,
  exponential: fitExponential,
};

export const fitCurve = (points, curveType) => {
  if (!points.length) return emptyResult(curveType);
  return FIT_FUNCTIONS[curveType](points);
};

// Fit all curve types and return map keyed by curve type
export const fitAllCurves = (points) => {
  const results = {};
  for (const { key } of CURVE_TYPES) {
    results[key] = points.length ? FIT_FUNCTIONS[key](points) : emptyResult(key);
  }
  return results;
};

// ── Estimated Time Data Processing ───────────────────────────────────
export const processEstimatedTimeData = (tasks, typeFilter) => {
  let valid = tasks.filter(isValidTask);

  if (typeFilter === 'video') {
    valid = valid.filter((t) => getTaskType(t) === 'video');
  } else if (typeFilter === 'presentation') {
    valid = valid.filter((t) => getTaskType(t) === 'presentation');
  }

  const enriched = valid
    .map((t) => {
      const totalSeconds = getCompletionSeconds(t);
      if (totalSeconds == null) return null;
      const processingSeconds = getProcessingSeconds(t);
      const queueSeconds = getQueueSeconds(t);
      // Use processing time (excluding queue) for formula; fall back to total if not available
      const seconds = processingSeconds != null ? processingSeconds : totalSeconds;
      const chars = t.script_char_count || 0;
      return {
        taskId: t.task_id,
        seconds,
        totalSeconds,
        processingSeconds,
        queueSeconds,
        hasProcessingTime: processingSeconds != null,
        resolution: getResolution(t),
        type: getTaskType(t),
        scriptChars: chars,
        charsPerSec: chars > 0 && seconds > 0 ? chars / seconds : 0,
      };
    })
    .filter(Boolean);

  // Distribution buckets grouped by resolution
  const distributionBuckets = TIME_BUCKETS.map((bucket) => {
    const row = { bucket: bucket.label };
    const inBucket = enriched.filter((d) => d.seconds >= bucket.min && d.seconds < bucket.max);
    row['1080p'] = inBucket.filter((d) => d.resolution === '1080p').length;
    row['720p'] = inBucket.filter((d) => d.resolution === '720p').length;
    return row;
  });

  // Scatter data — only video tasks with processing_started_at (pure processing time, excludes queue wait)
  const scatterData = enriched
    .filter((d) => d.scriptChars > 0 && d.hasProcessingTime && d.type === 'video')
    .map((d) => ({
      x: d.scriptChars,
      y: Math.round(d.processingSeconds * 10) / 10,
      resolution: d.resolution,
      type: d.type,
    }));

  // Chars per second by resolution & type
  const groups = {};
  for (const d of enriched) {
    if (d.charsPerSec <= 0) continue;
    const key = `${d.resolution}-${d.type}`;
    if (!groups[key]) groups[key] = { sum: 0, count: 0, resolution: d.resolution, type: d.type };
    groups[key].sum += d.charsPerSec;
    groups[key].count += 1;
  }
  const charsPerSecGroups = Object.values(groups).map((g) => ({
    label: `${g.resolution} / ${g.type}`,
    resolution: g.resolution,
    type: g.type,
    avgCharsPerSec: Math.round((g.sum / g.count) * 100) / 100,
    count: g.count,
  }));

  // Summary stats — processing time only (excludes tasks without processing_started_at)
  const tasks1080p = enriched.filter((d) => d.resolution === '1080p' && d.hasProcessingTime);
  const tasks720p = enriched.filter((d) => d.resolution === '720p' && d.hasProcessingTime);
  const avg = (arr) => (arr.length ? arr.reduce((s, d) => s + d.processingSeconds, 0) / arr.length : 0);
  const avgCps = enriched.length
    ? enriched.reduce((s, d) => s + d.charsPerSec, 0) / enriched.length
    : 0;
  const avgCharsAll = enriched.length
    ? enriched.reduce((s, d) => s + d.scriptChars, 0) / enriched.length
    : 0;

  // Regression per resolution — all curve types, processing time only (excludes queue wait)
  // Outlier removal: char_count in [10, 50000], processing time in [1, 3600]s
  const isInlier = (d) => d.scriptChars >= 10 && d.scriptChars <= 50000 && d.processingSeconds >= 1 && d.processingSeconds <= 3600;
  // Exclude presentations from video regression (presentations have different processing characteristics)
  const isVideoTask = (d) => d.type === 'video';
  const eligible1080p = enriched.filter((d) => d.resolution === '1080p' && d.hasProcessingTime && isVideoTask(d));
  const eligible720p = enriched.filter((d) => d.resolution === '720p' && d.hasProcessingTime && isVideoTask(d));
  const eligibleAll = enriched.filter((d) => d.hasProcessingTime && isVideoTask(d));
  const points1080p = eligible1080p.filter(isInlier).map((d) => ({ x: d.scriptChars, y: d.processingSeconds }));
  const points720p = eligible720p.filter(isInlier).map((d) => ({ x: d.scriptChars, y: d.processingSeconds }));
  const pointsAll = eligibleAll.filter(isInlier).map((d) => ({ x: d.scriptChars, y: d.processingSeconds }));
  const regressionOutliers = {
    '1080p': eligible1080p.length - points1080p.length,
    '720p': eligible720p.length - points720p.length,
    all: eligibleAll.length - pointsAll.length,
  };

  const curves1080p = fitAllCurves(points1080p);
  const curves720p = fitAllCurves(points720p);
  const curvesAll = fitAllCurves(pointsAll);

  // Queue time analysis
  const withQueue = enriched.filter((d) => d.queueSeconds != null);
  const avgQueue = withQueue.length
    ? Math.round(withQueue.reduce((s, d) => s + d.queueSeconds, 0) / withQueue.length * 10) / 10
    : null;
  const medianQueue = withQueue.length
    ? (() => {
        const sorted = withQueue.map((d) => d.queueSeconds).sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return Math.round((sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2) * 10) / 10;
      })()
    : null;
  const maxQueue = withQueue.length
    ? Math.round(Math.max(...withQueue.map((d) => d.queueSeconds)) * 10) / 10
    : null;
  const minQueue = withQueue.length
    ? Math.round(Math.min(...withQueue.map((d) => d.queueSeconds)) * 10) / 10
    : null;

  // Queue time distribution buckets
  const QUEUE_BUCKETS = [
    { label: '0-5s', min: 0, max: 5 },
    { label: '5-15s', min: 5, max: 15 },
    { label: '15-30s', min: 15, max: 30 },
    { label: '30-60s', min: 30, max: 60 },
    { label: '1-2m', min: 60, max: 120 },
    { label: '2-5m', min: 120, max: 300 },
    { label: '5m+', min: 300, max: Infinity },
  ];
  const queueDistribution = QUEUE_BUCKETS.map((bucket) => ({
    bucket: bucket.label,
    count: withQueue.filter((d) => d.queueSeconds >= bucket.min && d.queueSeconds < bucket.max).length,
  }));

  // Queue time scatter: script chars vs queue wait time
  const queueScatter = withQueue
    .filter((d) => d.scriptChars > 0)
    .map((d) => ({
      x: d.scriptChars,
      y: Math.round(d.queueSeconds * 10) / 10,
      resolution: d.resolution,
      type: d.type,
      taskId: d.taskId,
    }));

  // Tasks with vs without processing_started_at
  const tasksWithProcessingTime = enriched.filter((d) => d.hasProcessingTime).length;

  return {
    enriched,
    distributionBuckets,
    scatterData,
    charsPerSecGroups,
    totalTasks: enriched.length,
    avg1080p: Math.round(avg(tasks1080p) * 10) / 10,
    avg720p: Math.round(avg(tasks720p) * 10) / 10,
    avgCharsPerSec: Math.round(avgCps * 100) / 100,
    avgChars: Math.round(avgCharsAll),
    curves1080p,
    curves720p,
    curvesAll,
    // Queue time data
    queueStats: {
      total: withQueue.length,
      avg: avgQueue,
      median: medianQueue,
      max: maxQueue,
      min: minQueue,
    },
    queueDistribution,
    queueScatter,
    tasksWithProcessingTime,
    regressionOutliers,
  };
};

// ── User Consumption Data Processing ─────────────────────────────────
export const processUserConsumptionData = (tasks) => {
  const valid = tasks.filter(isValidTask);

  // Group by user
  const userMap = {};
  for (const t of valid) {
    const uid = t.user_id || 'unknown';
    if (!userMap[uid]) {
      userMap[uid] = {
        userId: uid,
        email: t.user_email || null,
        displayName: t.user_display_name || null,
        taskCount: 0,
        totalChars: 0,
        totalEstimatedCredits: 0,
        totalDeductedCredits: 0,
      };
    }
    const u = userMap[uid];
    // Capture email/display_name from first task that has it (LEFT JOIN may vary)
    if (!u.email && t.user_email) u.email = t.user_email;
    if (!u.displayName && t.user_display_name) u.displayName = t.user_display_name;
    u.taskCount += 1;
    u.totalChars += t.script_char_count || 0;
    u.totalEstimatedCredits += t.estimated_credits_used || 0;
    u.totalDeductedCredits += t.credits_deducted_seconds || 0;
  }

  const perUser = Object.values(userMap)
    .map((u) => ({
      ...u,
      avgChars: u.taskCount > 0 ? Math.round(u.totalChars / u.taskCount) : 0,
    }))
    .sort((a, b) => b.taskCount - a.taskCount);

  // Credit comparison (top 20 users by task count)
  const creditComparison = perUser.slice(0, 20).map((u) => ({
    userId: `${u.email || '—'} · ${u.userId.slice(0, 8)}…`,
    fullUserId: u.userId,
    email: u.email,
    displayName: u.displayName,
    estimated: Math.round(u.totalEstimatedCredits * 10) / 10,
    deducted: Math.round(u.totalDeductedCredits * 10) / 10,
  }));

  // Metadata averages
  const metaFields = {
    portrait_size: { sum: 0, count: 0 },
    background_size: { sum: 0, count: 0 },
    avatar_scale: { sum: 0, count: 0 },
    margin_from_center: { sum: 0, count: 0 },
    num_images: { sum: 0, count: 0 },
    num_texts: { sum: 0, count: 0 },
  };
  const metaByType = { video: JSON.parse(JSON.stringify(metaFields)), presentation: JSON.parse(JSON.stringify(metaFields)) };
  let totalSlides = 0, slidesCount = 0;
  const voiceCounts = {};
  const resolutionCounts = { '1080p': 0, '720p': 0, other: 0 };

  for (const t of valid) {
    const type = getTaskType(t);
    const meta = t.metadata || {};

    for (const field of Object.keys(metaFields)) {
      const val = meta[field];
      if (val != null && typeof val === 'number') {
        metaFields[field].sum += val;
        metaFields[field].count += 1;
        metaByType[type][field].sum += val;
        metaByType[type][field].count += 1;
      }
    }

    if (t.progress?.total_slides) {
      totalSlides += t.progress.total_slides;
      slidesCount += 1;
    }

    const voice = meta.voice_id;
    if (voice) voiceCounts[voice] = (voiceCounts[voice] || 0) + 1;

    const res = meta.resolution;
    if (res === '1080p') resolutionCounts['1080p'] += 1;
    else if (res === '720p') resolutionCounts['720p'] += 1;
    else resolutionCounts.other += 1;
  }

  const avgField = (field) => (field.count > 0 ? Math.round(field.sum / field.count) : null);

  const metadataAverages = Object.keys(metaFields).map((key) => ({
    metric: key,
    all: avgField(metaFields[key]),
    video: avgField(metaByType.video[key]),
    presentation: avgField(metaByType.presentation[key]),
  }));

  metadataAverages.push({
    metric: 'avg_slides (presentations)',
    all: slidesCount > 0 ? Math.round((totalSlides / slidesCount) * 10) / 10 : null,
    video: null,
    presentation: slidesCount > 0 ? Math.round((totalSlides / slidesCount) * 10) / 10 : null,
  });

  // Totals
  const totalEstimated = valid.reduce((s, t) => s + (t.estimated_credits_used || 0), 0);
  const totalDeducted = valid.reduce((s, t) => s + (t.credits_deducted_seconds || 0), 0);
  const creditAccuracy = totalEstimated > 0 ? Math.round((totalDeducted / totalEstimated) * 100) / 100 : null;

  // Credits regression: script_char_count → credits_deducted_seconds
  // Outlier removal: char_count in [10, 50000], credits (video duration) in [1, 600]s
  const creditsEligible = valid.filter((t) => (t.script_char_count || 0) > 0 && (t.credits_deducted_seconds || 0) > 0);
  const creditsPoints = creditsEligible
    .filter((t) => {
      const chars = t.script_char_count || 0;
      const credits = t.credits_deducted_seconds || 0;
      return chars >= 10 && chars <= 50000 && credits >= 1 && credits <= 600;
    })
    .map((t) => ({ x: t.script_char_count, y: t.credits_deducted_seconds }));
  const creditsCurves = fitAllCurves(creditsPoints);
  const creditsOutliers = creditsEligible.length - creditsPoints.length;

  const avgChars = valid.length
    ? Math.round(valid.reduce((s, t) => s + (t.script_char_count || 0), 0) / valid.length)
    : 0;

  return {
    perUser,
    creditComparison,
    metadataAverages,
    voiceCounts,
    resolutionCounts,
    totalUsers: perUser.length,
    totalTasks: valid.length,
    avgTasksPerUser: perUser.length > 0 ? Math.round((valid.length / perUser.length) * 10) / 10 : 0,
    totalCreditsEstimated: Math.round(totalEstimated * 10) / 10,
    totalCreditsDeducted: Math.round(totalDeducted * 10) / 10,
    creditAccuracy,
    creditsCurves,
    creditsOutliers,
    creditsRegressionCount: creditsPoints.length,
    avgChars,
  };
};

// ── Format Helpers ───────────────────────────────────────────────────
export const formatSeconds = (s) => {
  if (s == null || isNaN(s)) return '—';
  if (s < 60) return `${Math.round(s * 10) / 10}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};
